/**
 * File type validation constants and utilities
 */

// Supported document types
export const SUPPORTED_DOCUMENT_TYPES = {
  PDF: [
    'application/pdf',
    'application/x-pdf',
    'application/x-bzpdf', 
    'application/x-gzpdf'
  ],
  WORD: [
    'application/msword', // .doc (Word 97-2003)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx (Word 2007+)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.template', // .dotx (templates)
    'application/vnd.ms-word.template.macroEnabled.12', // .dotm (templates with macros)
    'application/vnd.ms-word.document.macroEnabled.12', // .docm (documents with macros)
    'application/rtf', // .rtf (Rich Text Format)
    'text/rtf' // .rtf (alternative MIME type)
  ],
  EXCEL: [
    'application/vnd.ms-excel', // .xls (Excel 97-2003)
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx (Excel 2007+)
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template', // .xltx (templates)
    'application/vnd.ms-excel.template.macroEnabled.12', // .xltm (templates with macros)
    'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm (spreadsheets with macros)
    'application/vnd.oasis.opendocument.spreadsheet' // .ods (OpenDocument Spreadsheet)
  ],
  POWERPOINT: [
    'application/vnd.ms-powerpoint', // .ppt (PowerPoint 97-2003)
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx (PowerPoint 2007+)
    'application/vnd.openxmlformats-officedocument.presentationml.slideshow', // .ppsx (PowerPoint slideshow)
    'application/vnd.ms-powerpoint.presentation.macroEnabled.12', // .pptm (PowerPoint with macros)
    'application/vnd.oasis.opendocument.presentation' // .odp (OpenDocument Presentation)
  ]
}

// All supported document types combined
export const ALL_DOCUMENT_TYPES = [
  ...SUPPORTED_DOCUMENT_TYPES.PDF,
  ...SUPPORTED_DOCUMENT_TYPES.WORD,
  ...SUPPORTED_DOCUMENT_TYPES.EXCEL,
  ...SUPPORTED_DOCUMENT_TYPES.POWERPOINT
]

// Allowed document file extensions for upload validation
export const ALLOWED_DOCUMENT_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'dotx', 'dotm', 'docm', 'rtf',
  'xls', 'xlsx', 'xltx', 'xltm', 'xlsm', 'ods',
  'ppt', 'pptx', 'ppsx', 'pptm', 'odp'
])

export const DOCUMENT_EXTENSION_GROUPS = {
  WORD: ['doc', 'docx', 'docm', 'dotx', 'dotm', 'rtf'],
  EXCEL: ['xls', 'xlsx', 'xlsm', 'xltx', 'xltm', 'ods'],
  POWERPOINT: ['ppt', 'pptx', 'ppsx', 'pptm', 'odp']
}

// Human-readable list of allowed extensions for validation messages
export const ALLOWED_EXTENSIONS_LABEL = [...ALLOWED_DOCUMENT_EXTENSIONS].map(e => e.toUpperCase()).join(', ')

// File size limits (in bytes)
export const FILE_SIZE_LIMITS = {
  MAX_DOCUMENT_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_IMAGE_SIZE: 5 * 1024 * 1024,      // 5MB
  MAX_TOTAL_SIZE: 50 * 1024 * 1024      // 50MB
}

// File type display names
export const FILE_TYPE_NAMES = {
  'application/pdf': 'PDF',
  'application/x-pdf': 'PDF',
  'application/x-bzpdf': 'PDF',
  'application/x-gzpdf': 'PDF',
  'application/msword': 'Word Document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template': 'Word Template',
  'application/vnd.ms-word.template.macroEnabled.12': 'Word Template',
  'application/vnd.ms-word.document.macroEnabled.12': 'Word Document',
  'application/rtf': 'Rich Text Format',
  'text/rtf': 'Rich Text Format',
  'application/vnd.ms-excel': 'Excel Spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': 'Excel Template',
  'application/vnd.ms-excel.template.macroEnabled.12': 'Excel Template',
  'application/vnd.ms-excel.sheet.macroEnabled.12': 'Excel Spreadsheet',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
  'application/vnd.ms-powerpoint': 'PowerPoint Presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow': 'PowerPoint Slideshow',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': 'PowerPoint Presentation',
  'application/vnd.oasis.opendocument.presentation': 'OpenDocument Presentation'
}

// File extensions by MIME type
export const FILE_EXTENSIONS = {
  'application/pdf': '.pdf',
  'application/x-pdf': '.pdf',
  'application/x-bzpdf': '.pdf',
  'application/x-gzpdf': '.pdf',
  'application/msword': '.doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template': '.dotx',
  'application/vnd.ms-word.template.macroEnabled.12': '.dotm',
  'application/vnd.ms-word.document.macroEnabled.12': '.docm',
  'application/rtf': '.rtf',
  'text/rtf': '.rtf',
  'application/vnd.ms-excel': '.xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.template': '.xltx',
  'application/vnd.ms-excel.template.macroEnabled.12': '.xltm',
  'application/vnd.ms-excel.sheet.macroEnabled.12': '.xlsm',
  'application/vnd.oasis.opendocument.spreadsheet': '.ods',
  'application/vnd.ms-powerpoint': '.ppt',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
  'application/vnd.openxmlformats-officedocument.presentationml.slideshow': '.ppsx',
  'application/vnd.ms-powerpoint.presentation.macroEnabled.12': '.pptm',
  'application/vnd.oasis.opendocument.presentation': '.odp'
}

// Helper function to get file extension from MIME type
export const getFileExtension = (mimeType) => {
  return FILE_EXTENSIONS[mimeType] || ''
}

// Helper function to get display name from MIME type
export const getFileTypeName = (mimeType) => {
  return FILE_TYPE_NAMES[mimeType] || 'Unknown'
}

// Helper function to check if file is PDF
export const isPDF = (mimeType) => {
  return SUPPORTED_DOCUMENT_TYPES.PDF.includes(mimeType)
}

// Helper function to check if file is Word
export const isWord = (mimeType) => {
  return SUPPORTED_DOCUMENT_TYPES.WORD.includes(mimeType)
}

// Helper function to check if file is PowerPoint
export const isPowerPoint = (mimeType) => {
  return SUPPORTED_DOCUMENT_TYPES.POWERPOINT.includes(mimeType)
}
